"use client";

import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
} from "recharts";
import { cn } from "cn";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "../ui/chart";

/**
 * The dashboard's charts: shadcn's chart (Recharts) with the dashboard's
 * look, so every chart (the overview's, the assistant's) is drawn the same
 * way. Colours are the theme's --chart-1…5.
 */

export type ChartPoint = { label: string; value: number };

/** A tiny area chart without axes, for a stat tile: the shape of the last weeks. */
export function Sparkline({
  data,
  label,
  color = "var(--chart-1)",
  className,
}: {
  data: ChartPoint[];
  label: string;
  color?: string;
  className?: string;
}) {
  const config = { value: { label, color } } satisfies ChartConfig;
  return (
    <ChartContainer
      config={config}
      className={cn("aspect-auto h-10 w-full", className)}
      initialDimension={{ width: 200, height: 40 }}
    >
      <AreaChart data={data} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient
            id={`spark-${label.replace(/\W/g, "")}`}
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop
              offset="0%"
              stopColor="var(--color-value)"
              stopOpacity={0.25}
            />
            <stop
              offset="100%"
              stopColor="var(--color-value)"
              stopOpacity={0}
            />
          </linearGradient>
        </defs>
        <ChartTooltip
          cursor={false}
          content={<ChartTooltipContent indicator="line" />}
        />
        <XAxis dataKey="label" hide />
        <Area
          dataKey="value"
          type="monotone"
          stroke="var(--color-value)"
          strokeWidth={1.5}
          fill={`url(#spark-${label.replace(/\W/g, "")})`}
          isAnimationActive={false}
        />
      </AreaChart>
    </ChartContainer>
  );
}

/** Bars per label (a month, a status, a content type), with a tooltip. */
export function BarsChart({
  data,
  label,
  color = "var(--chart-1)",
  className,
}: {
  data: ChartPoint[];
  label: string;
  color?: string;
  className?: string;
}) {
  const config = { value: { label, color } } satisfies ChartConfig;
  return (
    <ChartContainer
      config={config}
      className={cn("aspect-auto h-48 w-full", className)}
    >
      <BarChart data={data} margin={{ top: 8, right: 0, bottom: 0, left: -24 }}>
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="label"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          minTickGap={8}
        />
        <YAxis
          allowDecimals={false}
          tickLine={false}
          axisLine={false}
          width={48}
        />
        <ChartTooltip
          cursor={{ fill: "var(--muted)" }}
          content={<ChartTooltipContent />}
        />
        <Bar
          dataKey="value"
          fill="var(--color-value)"
          radius={[3, 3, 0, 0]}
          maxBarSize={36}
          isAnimationActive={false}
        />
      </BarChart>
    </ChartContainer>
  );
}
